import React from "react";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShield } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";

// TenantRoleShield — the counterpart marker to MasterRoleStar: every role
// that is NOT a LiftNet/MASTER role gets this shield instead, so a role
// picker/list never shows a bare, unmarked row — every option carries either
// the master star or this shield.
//
// Same reasoning as MasterRoleStar applies here: icon, color, spacing and
// tooltip copy all live in this one component so a redesign is a single
// edit instead of a hunt through every call site.
//
// Pair it with isMasterOrgRole / isMasterOrgId from utils/masterOrganization
// — this component only draws the marker, it never decides who gets one.

/** Icon and color of the marker — change these to restyle every call site. */
export const TENANT_ROLE_SHIELD_ICON = faShield;
export const TENANT_ROLE_SHIELD_COLOR = "#2968c0";

interface TenantRoleShieldProps {
  /**
   * Whether this role is NOT a master-organization role. Rendering nothing
   * when false is the component's job, so call sites stay a plain
   * `<TenantRoleShield show={…} />` instead of a conditional around it.
   */
  show?: boolean;
  /** Tooltip text; defaults to the translated "Tenant role". */
  title?: string;
  /** Extra styles merged over the marker's own color/margin. */
  style?: React.CSSProperties;
}

function TenantRoleShield({ show = true, title = "", style = {} }: TenantRoleShieldProps) {
  const [t] = useTranslation("global");

  if (!show) return null;

  return (
    <FontAwesomeIcon
      icon={TENANT_ROLE_SHIELD_ICON}
      style={{ color: TENANT_ROLE_SHIELD_COLOR, marginRight: 6, ...style }}
      title={title || t("organizationManagement.tenantRoleTooltip")}
    />
  );
}

export default TenantRoleShield;
